import {
  computed,
  type ComputedRef,
  getCurrentInstance,
  inject,
  type MaybeRef,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  type ShallowRef,
  shallowRef,
  unref,
  watch,
} from "vue";
import {
  DS_FORM_STORE_DOM_KEY,
  DS_FORM_STORE_PROVIDE_KEY,
  DS_FORM_CUSTOM_ELEMENT_SELECTOR,
} from "@/src/design_system/form_elements/form/constants/provide";
import type { DSFormStore } from "@/src/design_system/form_elements/form/store/dsFormStore";
import type { DSFormFieldValidationRule } from "@/src/design_system/form_elements/shared/types/validation";

interface UseDSFormFieldArgs<TValue> {
  name: MaybeRef<string | undefined>;
  value?: MaybeRef<TValue | undefined>;
  rules?: MaybeRef<DSFormFieldValidationRule<TValue>[] | undefined>;
  errorMessages?: MaybeRef<string[] | undefined>;
}

interface UseDSFormFieldReturn {
  errorMessages: ComputedRef<string[]>;
  validate: () => string[] | undefined;
  clearFieldErrors: () => void;
}

const resolveFormStoreFromDOM = async (): Promise<DSFormStore | null> => {
  const el = getCurrentInstance()?.vnode.el;
  if (!el) return null;
  const formHost = (el as HTMLElement).closest?.(DS_FORM_CUSTOM_ELEMENT_SELECTOR);
  if (!formHost) return null;
  await nextTick();
  return ((formHost as unknown as Record<string, unknown>)[DS_FORM_STORE_DOM_KEY] as DSFormStore) ?? null;
};

export const useDSFormField = <TValue>({
  name,
  value,
  rules,
  errorMessages,
}: UseDSFormFieldArgs<TValue>): UseDSFormFieldReturn => {
  const formStore: ShallowRef<DSFormStore | null> = shallowRef(null);
  formStore.value = inject(DS_FORM_STORE_PROVIDE_KEY, null);

  const normalizedRules = computed<DSFormFieldValidationRule<TValue>[]>(() => unref(rules) ?? []);
  const fieldValue = computed<TValue | undefined>(() => (value !== undefined ? unref(value) : undefined));
  const normalizedName = computed(() => {
    const nameValue: string | undefined = unref(name);
    if (!nameValue) return undefined;
    const nameMatch = nameValue.match(/\[(.*?)\]/);
    return nameMatch ? nameMatch[1] : nameValue;
  });

  const validate = (): string[] | undefined => {
    if (!normalizedRules.value.length || !normalizedName.value || !formStore.value) return undefined;

    const errors: string[] = [];

    normalizedRules.value.forEach(rule => {
      const result = rule(fieldValue.value);

      if (result === true || result === undefined || result === null) return;

      errors.push(result);
    });

    return errors.length ? errors : undefined;
  };

  const storeErrorMessages = computed<string[]>(() => {
    if (!normalizedName.value || !formStore.value) {
      return [];
    }

    return formStore.value.getFieldErrorMessages(normalizedName.value) ?? [];
  });

  const propErrorMessages = computed<string[]>(() => {
    return unref(errorMessages) ?? [];
  });

  const errorMessagesResult = computed<string[]>(() => {
    if (!storeErrorMessages.value.length && !propErrorMessages.value.length) {
      return [];
    }

    return [...storeErrorMessages.value, ...propErrorMessages.value];
  });

  const unregisterField = () => {
    if (!normalizedName.value || !formStore.value) return;
    formStore.value.unregisterField(normalizedName.value);
  };

  const registerField = () => {
    if (!normalizedName.value || !formStore.value) return;
    formStore.value.registerField(normalizedName.value, validate);
  };

  const clearFieldErrors = () => {
    if (!normalizedName.value || !formStore.value) return;
    formStore.value.resetFieldValidation(normalizedName.value);
  };

  watch(
    [() => (value !== undefined ? unref(value) : undefined), () => unref(rules)?.length ?? 0],
    ([newValue, newRulesLength], [oldValue, oldRulesLength]: [TValue | undefined, number] = [undefined, 0]) => {
      if (newValue !== oldValue || newRulesLength !== oldRulesLength) {
        clearFieldErrors();
      }
    }
  );

  registerField();

  onMounted(async () => {
    if (!formStore.value) {
      formStore.value = await resolveFormStoreFromDOM();
      registerField();
    }
  });

  onUpdated(() => {
    registerField();
  });

  onBeforeUnmount(() => {
    unregisterField();
  });

  return {
    errorMessages: errorMessagesResult,
    validate,
    clearFieldErrors,
  };
};
